/** 主辅模型发现接口的最小响应契约，兼容 auth.request 解包后的数据。 */
interface ModelListResponse {
  status: number;
  success: boolean;
  data?: unknown;
}

/** 保留平台全部元数据，交由调用方映射到应用配置。 */
type PlatformModel = Record<string, unknown> & { id: string };

/**
 * 拉取主、辅助模型全集供沙箱路由使用，主接口数据按 ID 优先。
 * request 必须复用当前用户的同一网关 API Key；仅辅助接口 404 兼容旧平台。
 * 鉴权、网络、业务及格式错误必须抛出，防止覆盖已缓存的完整清单。
 */
export async function fetchPlatformModelCatalog(
  request: (path: string) => Promise<ModelListResponse>,
): Promise<PlatformModel[]> {
  /** 解析单个接口，保留空数组与无效响应的区别。 */
  async function fetchList(path: string, auxiliary: boolean): Promise<PlatformModel[]> {
    const response = await request(path);
    if (auxiliary && response.status === 404) return [];
    if (!response.success || response.status < 200 || response.status >= 300) {
      throw new Error(`模型列表请求失败: ${path} (HTTP ${response.status})`);
    }
    const payload = response.data;
    const list = Array.isArray(payload)
      ? payload
      : payload && typeof payload === 'object' ? (payload as Record<string, unknown>).data : null;
    if (!Array.isArray(list)) throw new Error(`模型列表响应格式异常: ${path}`);
    return list.filter((model): model is PlatformModel =>
      model !== null && typeof model === 'object'
      && typeof model.id === 'string' && model.id.trim().length > 0,
    ).map(model => auxiliary ? { ...model, is_primary: false } : model);
  }

  const [primary, auxiliary] = await Promise.all([
    fetchList('/v1/models', false),
    fetchList('/v1/models/auxiliary', true),
  ]);
  const byId = new Map<string, PlatformModel>();
  for (const model of [...primary, ...auxiliary]) {
    if (!byId.has(model.id)) byId.set(model.id, model);
  }
  return [...byId.values()];
}
